import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle2, 
  TrendingUp, 
  Users, 
  Zap, 
  Shield, 
  Bot,
  Code,
  Cog,
  ShoppingCart,
  Megaphone,
  Star,
  Play,
  BadgeCheck,
  Sparkles,
  ChevronRight,
  X,
  Clock,
  Coins,
  Target as TargetIcon,
  Layers,
  Gauge,
  Lock,
  ScrollText,
  Handshake,
  Search,
  Lightbulb,
  Rocket,
  Settings,
  MapPin,
  FileCheck,
  Award,
  Quote,
  Globe
} from 'lucide-react';
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
  AnimatedCounter,
  FloatingElement,
  ScrollIndicator,
  AnimatedBlobs,
  VideoModal,
  InteractiveCard,
  WaveDivider,
  CurvedDivider
} from '../components/AnimationComponents';
import { HomepageIndustrySection } from '../components/IndustrySections';
import { getFeaturedCaseStudies } from './CaseStudiesPage';

const HERO_IMAGE = "https://images.unsplash.com/photo-1767465349271-d73ae06ceeff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHx2YW5jb3V2ZXIlMjBza3lsaW5lJTIwbW9kZXJuJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc0MjkxODU1fDA&ixlib=rb-4.1.0&q=85";
const HERO_VIDEO = "https://videos.pexels.com/video-files/32805734/13984928_2560_1440_60fps.mp4"; // Vancouver aerial drone (Pexels, free license)

// ===================== HERO SECTION =====================
const HeroSection = () => (
  <section 
    className="relative min-h-screen flex items-center overflow-hidden"
    data-testid="hero-section"
  >
    {/* Background: Vancouver skyline image (always) + subtle video overlay (desktop only) */}
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
    <video
      className="absolute inset-0 w-full h-full object-cover hidden md:block"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster={HERO_IMAGE}
      aria-hidden="true"
    >
      <source src={HERO_VIDEO} type="video/mp4" />
    </video>
    <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540]/95 via-[#0A2540]/90 to-[#0A2540]/80" />
    
    <div className="container-custom relative z-10 py-20">
      <div className="max-w-3xl">
        {/* Badge, replaced "Trusted by 200+" per feedback */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8"
        >
          <Shield className="w-4 h-4 text-[#2563EB]" />
          <span className="text-sm text-white/90">Built for Canadian Owner-Led Businesses</span>
        </motion.div>
        
        {/* Main Heading, updated per feedback */}
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="font-outfit text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]"
        >
          AI-Powered Growth for{' '}
          <span className="relative">
            <span className="text-[#2563EB]">Canadian Small and Medium Businesses</span>
            <motion.svg
              className="absolute -bottom-2 left-0 w-full"
              viewBox="0 0 300 12"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <motion.path
                d="M0 6 Q150 12 300 6"
                stroke="#2563EB"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
              />
            </motion.svg>
          </span>
        </motion.h1>
        
        {/* Subheading, updated per feedback */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-lg md:text-2xl text-white/85 mb-10 max-w-2xl leading-relaxed"
        >
          We help Canadian owner-led businesses build stronger client relationships, streamline operations, and adopt affordable AI with clarity and control.
        </motion.p>
        
        {/* CTA Buttons, "Get Free Consultation" → "Book a Discovery Call" */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link to="/contact">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Button 
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-[#2563EB]/25"
                data-testid="hero-cta-primary"
              >
                Book a Discovery Call
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </Link>
          <Link to="/services">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm"
                data-testid="hero-cta-secondary"
              >
                View Services
              </Button>
            </motion.div>
          </Link>
        </motion.div>
        
        {/* Stats, updated per feedback */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="flex flex-wrap items-center gap-8 mt-12 pt-8 border-t border-white/10"
        >
          <div>
            <div className="text-3xl font-bold text-white">
              <AnimatedCounter end={70} suffix="+" duration={2} />
            </div>
            <div className="text-sm text-white/70">Businesses Served</div>
          </div>
          <div className="w-px h-12 bg-white/20 hidden sm:block" />
          <div>
            <div className="text-3xl font-bold text-white">
              <AnimatedCounter end={37} suffix="%" duration={2} />
            </div>
            <div className="text-sm text-white/70">Avg Cost Reduction</div>
          </div>
          <div className="w-px h-12 bg-white/20 hidden sm:block" />
          <div>
            <div className="text-3xl font-bold text-white">
              <AnimatedCounter end={40} suffix="%" duration={2} />
            </div>
            <div className="text-sm text-white/70">Timeline Reduction</div>
          </div>
        </motion.div>
      </div>
    </div>
    
    <ScrollIndicator />
  </section>
);

// ===================== SOCIAL PROOF BAR (REMOVED, was duplicating hero stats) =====================
// Removed per client feedback to avoid duplication with hero stats.

// ===================== OPPORTUNITY SECTION (reframed from "Challenge") =====================
const OpportunitySection = () => {
  const opportunities = [
    {
      icon: Clock,
      title: "Reclaim 1+ hour a day",
      description: "Stop losing time to manual follow-ups, copying data, and chasing approvals.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Coins,
      title: "Get payback in under 2 years",
      description: "Automations should pay for themselves fast, not become another software expense.",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: TrendingUp,
      title: "Turn $1 into $1.60+",
      description: "Replace busywork with workflows that create measurable business value.",
      color: "from-violet-500 to-purple-500"
    },
    {
      icon: Gauge,
      title: "Reduce admin drag by 29%",
      description: "Free your team from repetitive operations and redirect effort to sales and delivery.",
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: Layers,
      title: "Close the digital gap",
      description: "Most SMBs use tools, but very few have them fully connected.",
      color: "from-rose-500 to-pink-500"
    },
    {
      icon: Shield,
      title: "Stay competitive in a high-cost market",
      description: "Automation helps offset rising wages, overhead, and cash-flow pressure.",
      color: "from-indigo-500 to-blue-500"
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" data-testid="opportunity-section">
      <div className="container-custom relative z-10">
        <FadeInUp className="text-center mb-16">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">The Opportunity</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-4">
            Six Ways SMBs Win Right Now
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Unprecedented times create unprecedented opportunities. Here&rsquo;s how Canadian SMBs are turning pressure into progress.
          </p>
        </FadeInUp>
        
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr" staggerDelay={0.1}>
          {opportunities.map((item, index) => (
            <StaggerItem key={index}>
              <InteractiveCard>
                <div 
                  className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all h-full group"
                  data-testid={`opportunity-card-${index}`}
                >
                  <motion.div 
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <item.icon className="w-7 h-7 text-white" />
                  </motion.div>
                  <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3 leading-tight">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              </InteractiveCard>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

// ===================== FOUNDER'S NOTE (mid-page emotional break, richer 2-column layout) =====================
const FounderNoteSection = () => (
  <section className="section-padding bg-[#0A2540] relative overflow-hidden" data-testid="founder-note-section">
    {/* Decorative background */}
    <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at 25% 25%, #2563EB 0%, transparent 40%), radial-gradient(circle at 75% 75%, #38BDF8 0%, transparent 40%)'
    }} />

    <div className="container-custom relative z-10">
      <div className="max-w-6xl mx-auto">
        <FadeInUp className="text-center mb-14">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">A Note from Our Founder</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mt-4 mb-4 leading-tight">
            Feeling the AI FOMO? You&rsquo;re not alone.
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            A quick, honest word before you keep reading.
          </p>
        </FadeInUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* LEFT: Founder identity card */}
          <FadeInUp delay={0.1} className="lg:col-span-4">
            <div className="sticky top-24">
              <div className="relative bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-3xl p-8 shadow-2xl overflow-hidden">
                {/* Decorative grid */}
                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)',
                  backgroundSize: '24px 24px'
                }} />

                {/* Founder headshot */}
                <div className="relative flex flex-col items-center text-center">
                  <div className="w-28 h-28 bg-white rounded-full flex items-center justify-center shadow-xl mb-5 overflow-hidden ring-4 ring-white/20">
                    <img
                      src="/images/team/harpal.webp"
                      alt="Harpal Singh — Founder, Blue Ribbon Technologies"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-white text-xl font-outfit font-semibold">Blue Ribbon Technologies</div>
                  <div className="text-white/80 text-sm mt-1">Founder&rsquo;s Note</div>

                  <div className="w-full h-px bg-white/20 my-6" />

                  {/* Stats mini-grid */}
                  <div className="grid grid-cols-2 gap-4 w-full">
                    <div>
                      <div className="text-2xl font-bold text-white">25+</div>
                      <div className="text-xs text-white/70">Years in software</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">70+</div>
                      <div className="text-xs text-white/70">Businesses served</div>
                    </div>
                  </div>

                  <div className="w-full h-px bg-white/20 my-6" />

                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>Vancouver, BC</span>
                  </div>
                </div>
              </div>

              <Link to="/about">
                <motion.div whileHover={{ x: 4 }} className="inline-flex items-center gap-2 text-[#38BDF8] font-medium mt-6 text-base">
                  Read our full story
                  <ArrowRight className="w-4 h-4" />
                </motion.div>
              </Link>
            </div>
          </FadeInUp>

          {/* RIGHT: The quote / letter */}
          <FadeInUp delay={0.25} className="lg:col-span-8">
            <div className="relative">
              <Quote className="absolute -top-4 -left-4 w-20 h-20 text-[#2563EB] opacity-20" />

              <div className="relative space-y-6 text-white/90 text-lg md:text-xl leading-relaxed font-light">
                <p>
                  Many business owners feel that FOMO around AI, but the truth is simpler than you think.
                </p>

                <p className="text-white">
                  <strong className="font-semibold">AI isn&rsquo;t about replacing your team, it&rsquo;s about freeing them to work smarter</strong> while you grow your business. Achieving more with less.
                </p>

                <p>
                  After 25+ years in the software industry, I started Blue Ribbon Technologies with a belief that technology should not be complex, 
                  <span className="text-[#38BDF8] font-medium"> it should simplify, accelerate, and empower.</span>
                </p>

                <p>
                  At BRT, our mission is to build effective, budget-friendly technology solutions that are intelligent and custom-tailored for your business needs, not the other way around.
                </p>
              </div>

              {/* Signature line */}
              <div className="mt-10 pt-6 border-t border-white/10 flex items-center gap-3">
                <div className="w-1 h-10 bg-[#2563EB] rounded-full" />
                <div>
                  <div className="font-outfit text-white font-medium text-base">The BRT Team</div>
                  <div className="text-white/60 text-sm">Vancouver, British Columbia</div>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </div>
    </div>
  </section>
);

// ===================== SERVICES SECTION (Comprehensive Digital Solutions) =====================
const ServicesSection = () => {
  const services = [
    { 
      icon: Bot, 
      title: "AI Solutions", 
      subtitle: "Intelligent Automation",
      description: "Practical AI at SMB-friendly prices. Chatbots, predictive insights, and automations that pay for themselves.",
      href: "/ai-solutions", 
      color: "from-blue-600 to-cyan-500",
      stat: "Payback in < 2 years"
    },
    { 
      icon: Code, 
      title: "Product Engineering", 
      subtitle: "Build What Matters",
      description: "Web apps, mobile apps, and custom software built to scale. From MVP to production-ready.",
      href: "/services/product-engineering", 
      color: "from-purple-600 to-pink-500",
      stat: "MVP in 4–6 weeks"
    },
    { 
      icon: Cog, 
      title: "Business Automation", 
      subtitle: "Work Smarter",
      description: "Eliminate manual work, connect your systems, and leverage internal knowledge access across your team.",
      href: "/services/business-automation", 
      color: "from-emerald-600 to-teal-500",
      stat: "40% timeline reduction"
    },
    { 
      icon: ShoppingCart, 
      title: "ERP & Ecommerce", 
      subtitle: "Unified Commerce",
      description: "Modern ERP systems and ecommerce platforms that grow with your business.",
      href: "/services/erp-ecommerce", 
      color: "from-orange-600 to-amber-500",
      stat: "3× faster order flow"
    },
    { 
      icon: Megaphone, 
      title: "Digital Marketing", 
      subtitle: "Get Found & Convert",
      description: "AI-powered SEO, GEO optimization, social, and paid ads tied to real revenue outcomes.",
      href: "/services/digital-marketing", 
      color: "from-rose-600 to-pink-500",
      stat: "4.2× Return on Ad Spend"
    },
    { 
      icon: Users, 
      title: "Consulting & Advisory", 
      subtitle: "Expert Guidance",
      description: "Strategic guidance for digital transformation, AI adoption, and scalability, vendor-agnostic and practical.",
      href: "/services/consulting", 
      color: "from-indigo-600 to-violet-500",
      stat: "Answers in 4–8 weeks"
    }
  ];

  return (
    <section className="section-padding bg-[#0A2540] relative overflow-hidden" data-testid="services-section">
      <AnimatedBlobs />
      <div className="container-custom relative z-10">
        <FadeInUp className="text-center mb-12">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">What We Offer</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mt-4 mb-4">
            Comprehensive Digital Solutions
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            From AI automation to product engineering, we help Canadian businesses build, grow, and compete.
          </p>
        </FadeInUp>
        
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr" staggerDelay={0.1}>
          {services.map((service, index) => (
            <StaggerItem key={service.title}>
              <Link to={service.href}>
                <motion.div
                  className="group bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all h-full"
                  whileHover={{ y: -8, borderColor: "rgba(37, 99, 235, 0.5)" }}
                  data-testid={`service-card-${index}`}
                >
                  <motion.div 
                    className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <service.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="font-outfit text-xl font-semibold text-white mb-1">{service.title}</h3>
                  <p className="text-[#2563EB] text-sm font-medium mb-3">{service.subtitle}</p>
                  <p className="text-white/70 text-base leading-relaxed mb-4">{service.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <span className="text-white/60 text-sm">{service.stat}</span>
                    <motion.span 
                      className="text-white/70 group-hover:text-white flex items-center gap-1 text-sm"
                      whileHover={{ x: 5 }}
                    >
                      Learn more <ArrowRight className="w-4 h-4" />
                    </motion.span>
                  </div>
                </motion.div>
              </Link>
            </StaggerItem>
          ))}
        </StaggerContainer>
        
        <FadeInUp delay={0.6} className="text-center mt-12">
          <Link to="/services">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="outline" 
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full px-8 py-6 text-lg"
              >
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </Link>
        </FadeInUp>
      </div>
    </section>
  );
};

// ===================== APPROACH TEASER (aligned with 5-phase Our Approach page) =====================
const ApproachTeaser = () => (
  <section className="section-padding relative overflow-hidden" data-testid="approach-teaser-section">
    <div className="container-custom">
      <FadeInUp className="text-center mb-16">
        <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Our Approach</span>
        <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-4">
          A 5-Phase Path to Meaningful Change
        </h2>
        <p className="text-slate-600 text-lg max-w-2xl mx-auto">
          Clear scope. Practical delivery. Measurable outcomes. Here&rsquo;s how we work with you from first conversation to continuous improvement.
        </p>
      </FadeInUp>
      
      <div className="relative">
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 auto-rows-fr" staggerDelay={0.15}>
          {[
            { step: "01", title: "Discovery & Assessment", description: "Analyze business processes to identify automation opportunities.", icon: Search, color: "bg-blue-500" },
            { step: "02", title: "Strategy & Planning", description: "Roadmap with clear milestones, deliverables, and success metrics.", icon: Lightbulb, color: "bg-purple-500" },
            { step: "03", title: "Implementation", description: "Agile delivery with regular check-ins, no black-box builds.", icon: Settings, color: "bg-emerald-500" },
            { step: "04", title: "Launch & Training", description: "Smooth go-live with training so your team owns the outcome.", icon: Rocket, color: "bg-orange-500" },
            { step: "05", title: "Ongoing Optimization", description: "Expand automation across your organization with continuous refinement.", icon: TrendingUp, color: "bg-rose-500" }
          ].map((item, index) => (
            <StaggerItem key={index}>
              <motion.div 
                className="relative group h-full"
                data-testid={`approach-step-${index}`}
                whileHover={{ y: -5 }}
              >
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all relative z-10 h-full">
                  <motion.div 
                    className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-5 mx-auto relative`}
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <item.icon className="w-7 h-7 text-white" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border border-slate-100">
                      <span className="text-sm font-bold text-[#0A2540]">{item.step}</span>
                    </div>
                  </motion.div>
                  <h3 className="font-outfit text-lg font-semibold text-[#0A2540] mb-2 text-center">{item.title}</h3>
                  <p className="text-slate-600 text-center text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
      
      <FadeInUp delay={0.6} className="text-center mt-12">
        <Link to="/approach">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="inline-block">
            <Button 
              variant="outline"
              className="border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white rounded-full px-8 py-6 text-lg"
              data-testid="approach-teaser-cta"
            >
              Explore Our Full Approach
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </Link>
      </FadeInUp>
    </div>
  </section>
);

// ===================== TRUST & TRANSPARENCY SECTION (NEW) =====================
const TrustTransparencySection = () => {
  const promises = [
    {
      icon: TargetIcon,
      title: "Clear scope. Clear outcomes.",
      description: "You know what is being built, why it matters, and how success will be measured."
    },
    {
      icon: Handshake,
      title: "Practical implementation.",
      description: "We prioritize changes that are realistic for owner-led teams and avoid unnecessary complexity."
    },
    {
      icon: TrendingUp,
      title: "Business-first delivery.",
      description: "Every engagement is designed around time saved, cost reduced, or revenue protected, not technical novelty."
    },
    {
      icon: ScrollText,
      title: "No inflated promises.",
      description: "If a workflow is not worth automating, we will say so."
    },
    {
      icon: Lock,
      title: "Strong data protection.",
      description: "Robust non-disclosure agreements and confidentiality terms protect your business data and client relationships."
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-br from-slate-50 via-white to-slate-50 relative overflow-hidden" data-testid="trust-transparency-section">
      <div className="container-custom relative z-10">
        <FadeInUp className="text-center mb-16 max-w-3xl mx-auto">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Trust & Transparency</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-6 leading-tight">
            How We Show Up for Every Client
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            Consulting has a trust problem. We fix it by being explicit about what we do, why we do it, and how we prove it worked.
          </p>
        </FadeInUp>
        
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto auto-rows-fr" staggerDelay={0.1}>
          {promises.map((promise, i) => (
            <StaggerItem key={i}>
              <motion.div
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all h-full"
                whileHover={{ y: -5, borderColor: "#2563EB" }}
                data-testid={`trust-promise-${i}`}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-xl flex items-center justify-center mb-5">
                  <promise.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3 leading-tight">{promise.title}</h3>
                <p className="text-slate-600 text-base leading-relaxed">{promise.description}</p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

// ===================== VIDEO TESTIMONIALS (heading generalized, non-AI-specific) =====================
const VideoTestimonialsSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  const videoTestimonials = [
    {
      id: 1,
      name: "Brenda De Silva",
      role: "Founder",
      company: "Papaya Health",
      location: "Vancouver, BC",
      thumbnail: "/images/testimonials/brenda-papaya.jpg",
      project: "Mobile App Development",
      quote: "Blue Ribbon helped us rethink how we run day-to-day.",
      videoUrl: "https://customer-assets-m6fa6gv7.emergentagent.net/job_ai-transform-ca/artifacts/0waw5xa0_Papaya%20Health%20-%20Brenda.mp4"
    },
    {
      id: 2,
      name: "Michelle Deschenes",
      role: "Founder",
      company: "ACE Nutrition",
      location: "Vancouver, BC",
      thumbnail: "/images/testimonials/michelle-ace.jpg",
      project: "B2B Lead Generation",
      quote: "Our team finally has time to focus on clients.",
      videoUrl: "/videos/ace-nutrition-michelle.mp4"
    },
    {
      id: 3,
      name: "Roxanne",
      role: "Owner",
      company: "LB Electric",
      location: "Canada",
      thumbnail: "/images/testimonials/roxanne-lb-electric.jpg",
      project: "Website & Digital Marketing",
      quote: "Clearer positioning and steady inbound momentum.",
      videoUrl: "/videos/roxanne-lb-electric.mp4"
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-white to-slate-50 relative" data-testid="video-testimonials-section">
      <div className="container-custom">
        <FadeInUp className="text-center mb-16">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Client Stories</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-4">
            Hear From Our Clients
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Real Canadian businesses. Real results across automation, marketing, engineering, and advisory work.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto auto-rows-fr" staggerDelay={0.15}>
          {videoTestimonials.map((testimonial, index) => (
            <StaggerItem key={testimonial.id}>
              <motion.div
                className={`group relative bg-white rounded-2xl overflow-hidden shadow-lg transition-all ${testimonial.comingSoon ? 'opacity-90' : 'hover:shadow-2xl cursor-pointer'}`}
                onClick={() => !testimonial.comingSoon && setActiveVideo(testimonial)}
                whileHover={testimonial.comingSoon ? {} : { y: -8 }}
                data-testid={`video-testimonial-${index}`}
              >
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={testimonial.thumbnail} 
                    alt={testimonial.name}
                    className={`w-full h-full object-cover transition-transform duration-500 ${testimonial.comingSoon ? 'grayscale' : 'group-hover:scale-110'}`}
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                  
                  {testimonial.comingSoon ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/95 backdrop-blur-sm px-5 py-3 rounded-full">
                        <span className="text-sm font-semibold text-[#0A2540]">Coming Soon</span>
                      </div>
                    </div>
                  ) : (
                    <motion.div 
                      className="absolute inset-0 flex items-center justify-center"
                      whileHover={{ scale: 1.1 }}
                    >
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                        <Play className="w-7 h-7 text-[#2563EB] ml-1" fill="#2563EB" />
                      </div>
                    </motion.div>
                  )}
                  
                  {!testimonial.comingSoon && (
                    <div className="absolute top-4 left-4">
                      <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        <BadgeCheck className="w-4 h-4 text-[#2563EB]" />
                        <span className="text-xs font-medium text-[#0A2540]">Verified Client</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-[#0A2540] text-lg">{testimonial.name}</h3>
                      <p className="text-sm text-slate-600">{testimonial.role}</p>
                      <p className="text-sm text-slate-500">{testimonial.company}</p>
                      {testimonial.project && (
                        <p
                          className="mt-2 text-sm font-semibold text-emerald-600"
                          data-testid={`video-testimonial-project-${index}`}
                        >
                          {testimonial.project}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <span className="text-xs text-slate-500">{testimonial.location}</span>
                    {testimonial.metric && (
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                        {testimonial.metric}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Written testimonials, kept generic */}
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-fr" staggerDelay={0.1}>
          {[
            { quote: "Reduced costs by 35%", author: "— Manufacturing Client" },
            { quote: "Saved 15+ hrs/week", author: "— Retail Business" },
            { quote: "Clearer, calmer operations", author: "— Services Company" }
          ].map((item, index) => (
            <StaggerItem key={index}>
              <motion.div 
                className="bg-slate-100 py-14 px-6 rounded-xl text-center h-full min-h-[200px] flex flex-col items-center justify-center"
                whileHover={{ scale: 1.02 }}
              >
                <p className="text-2xl font-bold text-[#0A2540] mb-3 leading-tight">{item.quote}</p>
                <p className="text-slate-600 text-sm">{item.author}</p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>

      <VideoModal
        isOpen={!!activeVideo}
        onClose={() => setActiveVideo(null)}
        videoUrl={activeVideo?.videoUrl}
        title={activeVideo ? `${activeVideo.name} - ${activeVideo.company}` : ''}
      />
    </section>
  );
};

// ===================== WHY CHOOSE US (rich layout, anchored in founder's concrete differentiators) =====================
const WhyChooseUsSection = () => {
  const reasons = [
    {
      icon: MapPin,
      title: "Local BC-Based Team",
      description: "We're right here in Vancouver, BC, just like you. Easy to meet, talk, and work with. No time-zone gymnastics, no offshore hand-offs.",
      stat: "BC",
      statLabel: "Vancouver-based"
    },
    {
      icon: Award,
      title: "Business + Tech Fluency",
      description: "Experienced software professionals with real industry knowledge. We sit at the intersection of technology and business, advising on solutions that deliver real value, not just code.",
      stat: "25+ yrs",
      statLabel: "Years in software"
    },
    {
      icon: Coins,
      title: "Affordable, Tailored Solutions",
      description: "Business-effective technology customized to your needs, without enterprise-grade price tags. We match ambition to budget.",
      stat: "37%",
      statLabel: "Avg cost reduction"
    },
    {
      icon: FileCheck,
      title: "Transparent Contracts, BC Law",
      description: "No confusing offshore terms. Your engagements are governed by British Columbia and Canadian law, clear, enforceable, and local.",
      stat: "100%",
      statLabel: "BC / Canadian jurisdiction"
    },
    {
      icon: Lock,
      title: "Clear IP Ownership",
      description: "Your intellectual property and deliverables belong to you, protected locally, documented explicitly, transferred without friction.",
      stat: "You own it",
      statLabel: "IPR clarity"
    },
    {
      icon: Globe,
      title: "Local PM + Quality-First Global Delivery",
      description: "BC-based project managers oversee your work while trusted global teams keep costs low. Practical advice with real business impact, solutions that save time, money, and headaches.",
      stat: "Quality",
      statLabel: "Quality-first delivery"
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-br from-slate-50 via-white to-slate-50 relative overflow-hidden" data-testid="why-choose-us-section">
      <div className="container-custom relative z-10">
        <FadeInUp className="text-center mb-16 max-w-3xl mx-auto">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Why Choose Blue Ribbon</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-6 leading-tight">
            Six Reasons SMBs Choose Us
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            We&rsquo;re not a big-firm consultancy with big-firm pricing. We&rsquo;re a local Vancouver team that builds practical, business-first solutions with honesty, speed, and measurable outcomes.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto auto-rows-fr" staggerDelay={0.1}>
          {reasons.map((reason, i) => (
            <StaggerItem key={i}>
              <motion.div
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-2xl transition-all h-full group relative overflow-hidden"
                whileHover={{ y: -6, borderColor: "#2563EB" }}
                data-testid={`why-choose-us-card-${i}`}
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#2563EB]/5 to-transparent rounded-bl-full pointer-events-none" />

                <div className="flex items-start justify-between mb-5">
                  <motion.div
                    className="w-14 h-14 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-xl flex items-center justify-center shadow-lg shadow-[#2563EB]/20"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <reason.icon className="w-7 h-7 text-white" />
                  </motion.div>
                  <div className="text-right">
                    <div className="text-xl md:text-2xl font-bold text-[#0A2540] leading-none">{reason.stat}</div>
                    <div className="text-xs text-slate-500 mt-1">{reason.statLabel}</div>
                  </div>
                </div>

                <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3 leading-tight">
                  {reason.title}
                </h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  {reason.description}
                </p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <FadeInUp delay={0.4} className="text-center mt-12">
          <Link to="/contact">
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} className="inline-block">
              <Button
                className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg"
                data-testid="why-choose-us-cta"
              >
                See if we&rsquo;re the right fit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </Link>
        </FadeInUp>
      </div>
    </section>
  );
};

// ===================== CLOSING CTA (enhanced with 3 discovery-call deliverables) =====================
const CTASection = () => (
  <section className="section-padding bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] relative overflow-hidden" data-testid="cta-section">
    <AnimatedBlobs />
    <div className="container-custom text-center relative z-10">
      <FadeInUp>
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 mb-8">
          <Sparkles className="w-4 h-4 text-yellow-300" />
          <span className="text-sm text-white">30-Minute Discovery Call · No obligation</span>
        </div>
      </FadeInUp>
      
      <FadeInUp delay={0.2}>
        <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-6 max-w-3xl mx-auto leading-tight">
          Start with one high-value workflow.
        </h2>
      </FadeInUp>
      
      <FadeInUp delay={0.4}>
        <p className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
          The fastest path to better operations is not a full transformation program. It is one well-chosen automation which is easier to adopt, creates visible value, and earns trust from the team.
        </p>
      </FadeInUp>

      {/* 3 discovery-call deliverables, from founder's content */}
      <FadeInUp delay={0.5}>
        <div className="max-w-3xl mx-auto mb-10">
          <p className="text-white/80 text-sm uppercase tracking-wider mb-5">On the call, we&rsquo;ll show you:</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Bot, label: "How AI can save you time and money" },
              { icon: Zap, label: "Quick automation wins for your business" },
              { icon: Coins, label: "Where you can cut costs without sacrificing quality" }
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 flex items-start gap-3 text-left" data-testid={`cta-benefit-${i}`}>
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-white text-base leading-snug">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </FadeInUp>
      
      <FadeInUp delay={0.7}>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Button 
                className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-medium shadow-xl"
                data-testid="cta-primary-button"
              >
                Book Your 30-Minute Discovery Call
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </Link>
          <Link to="/case-studies">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full px-8 py-6 text-lg font-medium"
                data-testid="cta-secondary-button"
              >
                View Success Stories
              </Button>
            </motion.div>
          </Link>
        </div>
      </FadeInUp>

      <FadeInUp delay={0.9}>
        <p className="text-white/70 text-sm mt-8 max-w-xl mx-auto italic">
          AI doesn&rsquo;t have to be complicated. You don&rsquo;t have to fall behind. Let us show you simple, budget-friendly ways to use technology to grow your business.
        </p>
      </FadeInUp>
    </div>
  </section>
);

// ===================== FEATURED CASE STUDIES (NEW) =====================
const FeaturedCaseStudiesSection = () => {
  const studies = getFeaturedCaseStudies(3);
  return (
    <section className="section-padding bg-white relative overflow-hidden" data-testid="featured-case-studies-section">
      <div className="container-custom relative z-10">
        <FadeInUp className="text-center mb-14 max-w-3xl mx-auto">
          <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Case Studies</span>
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-4 leading-tight">
            Real Businesses. Real Results.
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            See how Canadian owner-led businesses have transformed their operations with practical AI, automation, and search-ready digital work.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto auto-rows-fr" staggerDelay={0.1}>
          {studies.map((study, i) => (
            <StaggerItem key={study.id}>
              <Link to={`/case-studies/${study.slug || study.id}`}>
                <motion.article
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-2xl transition-all h-full flex flex-col"
                  whileHover={{ y: -6, borderColor: '#2563EB' }}
                  data-testid={`home-case-study-${i}`}
                >
                  <div className="aspect-video overflow-hidden bg-slate-100">
                    <img
                      src={study.image_url}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-xs font-medium text-[#2563EB] bg-[#2563EB]/10 px-3 py-1 rounded-full">
                        {study.industry}
                      </span>
                      {study.location && (
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-3 py-1 rounded-full inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {study.location}
                        </span>
                      )}
                    </div>
                    <h3 className="font-outfit text-lg font-semibold text-[#0A2540] mb-2 leading-tight line-clamp-2 group-hover:text-[#2563EB] transition-colors">
                      {study.title}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">{study.client_name}</p>
                    <div className="mt-auto flex items-center gap-4 pt-4 border-t border-slate-100 flex-wrap">
                      {Object.entries(study.metrics || {}).slice(0, 2).map(([k, v]) => (
                        <div key={k}>
                          <div className="text-lg font-bold text-[#2563EB]">{v}</div>
                          <div className="text-xs text-slate-500 capitalize">{k.replace(/_/g, ' ')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.article>
              </Link>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <FadeInUp delay={0.3} className="text-center mt-12">
          <Link to="/case-studies">
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} className="inline-block">
              <Button
                variant="outline"
                className="border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white rounded-full px-8 py-6 text-lg"
                data-testid="home-case-studies-cta"
              >
                View All Case Studies
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </Link>
        </FadeInUp>
      </div>
    </section>
  );
};

// ===================== HOME PAGE ROOT =====================
export const HomePage = () => {
  return (
    <div data-testid="home-page">
      <HeroSection />
      {/* Services (Comprehensive Digital Solutions) */}
      <ServicesSection />
      <WaveDivider color="#FFFFFF" flip />
      {/* Six Ways SMBs Win */}
      <OpportunitySection />
      <WaveDivider color="#FFFFFF" />
      {/* 5-phase Approach teaser */}
      <ApproachTeaser />
      {/* Founder's Note, mid-page emotional break with richer 2-column layout */}
      <FounderNoteSection />
      <CurvedDivider color="#F8FAFC" />
      {/* Trust & Transparency */}
      <TrustTransparencySection />
      <HomepageIndustrySection />
      <VideoTestimonialsSection />
      {/* Featured Case Studies (proof section) */}
      <FeaturedCaseStudiesSection />
      {/* Why Choose Us, anchored in founder's concrete BC differentiators */}
      <WhyChooseUsSection />
      <CTASection />
    </div>
  );
};

export default HomePage;
